import React from "react";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import TicketListing, { PremiumSLADisplay } from "./TicketListing";
import TicketListingWrapper from "./TicketListingWrapper";
import WorkCompletionModal from "./WorkCompletionModal";
import ProgressUpdateModal from "./ProgressUpdateModal";
import "../../styles/ticket-system/supervisor-dashboard.css";
import { formatDate } from "../../utils/utilityfunc";
import {
  getPriorityBadgeColor,
  getStatusBadgeColor,
} from "./utils/getStatusColor";
import PropTypes from "prop-types";
import PaginationModified from "../../data/PaginationModified";
import ReadMoreDescription from "../common-components/ReadMoreDescription";

const CompactTicketCard = ({
  ticket,
  showCompleteButton = false,
  onStartWork,
  onCompleteWork,
  onViewDetails,
  onUpdateProgress,
  isLoading,
  workingOnTickets,
}) => (
  <Card className="ticket-card-compact">
    <Card.Body>
      {/* Header Section with improved mobile layout */}
      <div className="ticket-header-section">
        <div
          style={{
            display: "flex",
            gap: "5px",
            alignItems: "flex-end",
          }}
        >
          <div className="ticket-info-section">
            <h6 className="mb-1 text-primary fw-bold">
              #{ticket.ticket_no || ticket.uid}
            </h6>
            <p className="mb-0 text-muted">
              <strong>Gym:</strong> {ticket?.gym_name || "N/A"}
            </p>
            <div className="ticket-description">
              <b>Issue Type:</b> {ticket?.issue_type_name}
            </div>
            <div>
              <b>⚠️ SLA STATUS</b>
              <PremiumSLADisplay ticket={ticket} />
            </div>
          </div>
        </div>

        <div className="ticket-badges-section">
          <Badge bg={getPriorityBadgeColor(ticket?.priority)}>
            {ticket?.priority || "N/A"}
          </Badge>
          <Badge bg={getStatusBadgeColor(ticket?.status)}>
            {ticket?.status?.replace("_", " ") || "N/A"}
          </Badge>
        </div>
      </div>

      {/* Actions Section with mobile-first design */}
      <div className="ticket-actions-section">
        <div className="ticket-meta-info">
          <small className="text-muted">
            <i className="fas fa-calendar me-1"></i>
            {formatDate(ticket?.created_at)}
          </small>
          {ticket?.category && (
            <small className="text-muted">
              <i className="fas fa-tag me-1"></i>
              {ticket.category}
            </small>
          )}
        </div>

        <div className="ticket-action-buttons">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => onViewDetails(ticket)}
          >
            <i className="fas fa-eye"></i>
            View Details
          </Button>

          {ticket.status === "assigned" && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => onStartWork(ticket.uid)}
              disabled={workingOnTickets[ticket.uid] === "starting"}
            >
              {workingOnTickets[ticket.uid] === "starting" ? (
                <Spinner size="sm" />
              ) : (
                <>
                  <i className="fas fa-play"></i>
                  Start Work
                </>
              )}
            </Button>
          )}

          {ticket.status === "work_started" && (
            <>
              <Button
                size="sm"
                variant="danger"
                onClick={() => onUpdateProgress(ticket)}
                disabled={workingOnTickets[ticket.uid] === "updating"}
              >
                {workingOnTickets[ticket.uid] === "updating" ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i className="fas fa-edit"></i>
                    Update Progress
                  </>
                )}
              </Button>

              {showCompleteButton && (
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => onCompleteWork(ticket)}
                  disabled={workingOnTickets[ticket.uid] === "completing"}
                >
                  {workingOnTickets[ticket.uid] === "completing" ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="fas fa-check"></i>
                      Mark as Completed
                    </>
                  )}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Card.Body>
  </Card>
);

const SupervisorDashboard = ({
  tickets = [],
  isLoading = false,
  activeTab,
  onStartWork,
  onCompleteWork,
  onViewDetails,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  startDate,
  endDate,
  onDateChange,
  isPage,
  setIsPage,
  pageLimit,
  setPageLimit,
  tablePagination = [],
  userInfoData,
  workingOnTickets = {},
  // New filter props
  issueTypeFilter,
  setIssueTypeFilter,
  raisedByFilter,
  setRaisedByFilter,
  supervisorFilter,
  setSupervisorFilter,
  gymFilter,
  setGymFilter,
  breachedFilter,
  setBreachedFilter,
  issueTypeOptions,
  raisedByOptions,
  supervisorOptions,
  onResetFilters,
}) => {
  // Function to determine if status filter should be hidden
  const shouldHideStatusFilter = (tabKey) => {
    // Define tabs where status filter should be hidden (tabs with predefined status)
    const tabsWithPredefinedStatus = [
      "assigned", // assigned status
      "in_progress", // work_started status
      "completed", // work_completed status
      "sla_breached", // sla_breached status
    ];

    return tabsWithPredefinedStatus.includes(tabKey);
  };

  // State for work completion modal
  const [isWorkCompletionModalOpen, setIsWorkCompletionModalOpen] =
    React.useState(false);
  const [selectedTicketForCompletion, setSelectedTicketForCompletion] =
    React.useState(null);

  // State for progress update modal
  const [isProgressUpdateModalOpen, setIsProgressUpdateModalOpen] =
    React.useState(false);
  const [selectedTicketForProgress, setSelectedTicketForProgress] =
    React.useState(null);

  // Handle complete work button click
  const handleCompleteClick = (ticket) => {
    setSelectedTicketForCompletion(ticket);
    setIsWorkCompletionModalOpen(true);
  };

  // Handle work completion modal close
  const handleWorkCompletionModalClose = () => {
    setIsWorkCompletionModalOpen(false);
    setSelectedTicketForCompletion(null);
  };

  // Handle work completion submit
  const handleWorkCompletionSubmit = () => {
    handleWorkCompletionModalClose();
    // Refresh the tickets
    if (onCompleteWork) {
      onCompleteWork();
    }
  };

  // Handle progress update button click
  const handleProgressUpdateClick = (ticket) => {
    setSelectedTicketForProgress(ticket);
    setIsProgressUpdateModalOpen(true);
  };

  // Handle progress update modal close
  const handleProgressUpdateModalClose = () => {
    setIsProgressUpdateModalOpen(false);
    setSelectedTicketForProgress(null);
  };

  // Handle progress update submit
  const handleProgressUpdateSubmit = () => {
    handleProgressUpdateModalClose();
    // Refresh the tickets
    if (onCompleteWork) {
      onCompleteWork();
    }
  };

  const isAllTicketsTab = activeTab === "all";
  const isSlaBreachedTab = activeTab === "sla_breached";

  // Common props for TicketListingWrapper
  const commonTicketListingProps = {
    tickets,
    userInfoData,
    isLoading,
    onTicketClick: onViewDetails,
    onTicketAction: (ticketId, action) => {
      if (action === "start") {
        onStartWork(ticketId);
      } else if (action === "complete") {
        onCompleteWork(ticketId);
      }
    },
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    startDate,
    endDate,
    onDateChange,
    isPage,
    setIsPage,
    pageLimit,
    setPageLimit,
    tablePagination,
    issueTypeFilter,
    setIssueTypeFilter,
    raisedByFilter,
    setRaisedByFilter,
    supervisorFilter,
    setSupervisorFilter,
    gymFilter,
    setGymFilter,
    breachedFilter,
    setBreachedFilter,
    issueTypeOptions,
    raisedByOptions,
    supervisorOptions,
    onResetFilters,
  };

  return (
    <div className="supervisor-dashboard">
      {isAllTicketsTab || isSlaBreachedTab ? (
        // Use TicketListingWrapper for "All Tickets" and "SLA Breached" tabs
        <TicketListingWrapper
          {...commonTicketListingProps}
          hideStatusFilter={shouldHideStatusFilter(activeTab)}
        />
      ) : (
        // Use card-based UI for other tabs (assigned, in_progress, completed)
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    {activeTab === "assigned"
                      ? "Assigned Tickets"
                      : activeTab === "in_progress"
                      ? "In Progress Tickets"
                      : activeTab === "completed"
                      ? "Completed Tickets"
                      : "All Tickets"}
                  </h5>
                </div>
              </Card.Header>
              <Card.Body>
                {isLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <div className="mt-2">Loading tickets...</div>
                  </div>
                ) : tickets.length === 0 ? (
                  <Alert variant="info">No tickets found</Alert>
                ) : (
                  <div>
                    {/* Ticket Cards */}
                    <div>
                      {tickets.map((ticket) => (
                        <CompactTicketCard
                          key={ticket.uid}
                          ticket={ticket}
                          showCompleteButton={activeTab === "in_progress"}
                          onStartWork={onStartWork}
                          onCompleteWork={handleCompleteClick}
                          onViewDetails={onViewDetails}
                          onUpdateProgress={handleProgressUpdateClick}
                          workingOnTickets={workingOnTickets}
                        />
                      ))}
                    </div>

                    {isPage || setIsPage || tablePagination ? (
                      <div className="customTablePaginationContainer">
                        <PaginationModified
                          isPage={isPage}
                          onChangePage={setIsPage}
                          pagination={tablePagination}
                          pageLimit={pageLimit}
                          setPageLimit={setPageLimit}
                        />
                      </div>
                    ) : null}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Work Completion Modal */}
      <WorkCompletionModal
        isOpen={isWorkCompletionModalOpen}
        onClose={handleWorkCompletionModalClose}
        ticket={selectedTicketForCompletion}
        onSubmit={handleWorkCompletionSubmit}
        userInfoData={userInfoData}
      />

      {/* Progress Update Modal */}
      <ProgressUpdateModal
        isOpen={isProgressUpdateModalOpen}
        onClose={handleProgressUpdateModalClose}
        ticket={selectedTicketForProgress}
        onSubmit={handleProgressUpdateSubmit}
        userInfoData={userInfoData}
      />
    </div>
  );
};

SupervisorDashboard.propTypes = {
  tickets: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  activeTab: PropTypes.string.isRequired,
  onStartWork: PropTypes.func.isRequired,
  onCompleteWork: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
  setStatusFilter: PropTypes.func.isRequired,
  priorityFilter: PropTypes.string,
  setPriorityFilter: PropTypes.func.isRequired,
  isPage: PropTypes.number.isRequired,
  setIsPage: PropTypes.func.isRequired,
  pageLimit: PropTypes.number.isRequired,
  setPageLimit: PropTypes.func.isRequired,
  workingOnTickets: PropTypes.object,
  tablePagination: PropTypes.array,
  userInfoData: PropTypes.object,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onDateChange: PropTypes.func,
  // New filter props
  issueTypeFilter: PropTypes.object,
  setIssueTypeFilter: PropTypes.func,
  raisedByFilter: PropTypes.object,
  setRaisedByFilter: PropTypes.func,
  supervisorFilter: PropTypes.object,
  setSupervisorFilter: PropTypes.func,
  gymFilter: PropTypes.object,
  setGymFilter: PropTypes.func,
  breachedFilter: PropTypes.object,
  setBreachedFilter: PropTypes.func,
  issueTypeOptions: PropTypes.array,
  raisedByOptions: PropTypes.array,
  supervisorOptions: PropTypes.array,
  onResetFilters: PropTypes.func,
};

export default React.memo(SupervisorDashboard);
